import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, AlertCircle, PenLine } from 'lucide-react';
import { formatApiErrorDetail } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import SignaturePad from '../components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RecordFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { recordId } = useParams();
  const isEditMode = Boolean(recordId);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [clients, setClients] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [showSignature, setShowSignature] = useState(false);
  const [signatureData, setSignatureData] = useState('');
  
  const [formData, setFormData] = useState({
    client_id: searchParams.get('client_id') || '',
    facility_id: '',
    service_type: '日中一時支援',
    service_date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00',
    content: '',
    staff_id: '',
    staff_name: '',
    notes: '',
    has_meal: false,
    has_bath: false,
    has_pickup: false,
    is_summer: false,
  });

  const serviceTypeOptions = [
    { value: '日中一時支援', label: '日中一時支援' },
    { value: '移動支援', label: '移動支援' },
    { value: '地域生活支援', label: '地域生活支援' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        staff_id: user.id,
        staff_name: user.name,
        facility_id: user.facility_id || prev.facility_id
      }));
    }
  }, [user]);

  useEffect(() => {
    if (isEditMode) {
      fetchRecord();
    }
  }, [recordId]);

  const fetchRecord = async () => {
    try {
      setInitialLoading(true);
      const res = await axios.get(`${API}/support-records/${recordId}/detail`);
      const r = res.data;
      setFormData({
        client_id: r.client_id || '',
        facility_id: r.facility_id || '',
        service_type: r.service_type || '日中一時支援',
        service_date: r.service_date || '',
        start_time: r.start_time || '',
        end_time: r.end_time || '',
        content: r.content || '',
        staff_id: r.staff_id || '',
        staff_name: r.staff_name || '',
        notes: r.notes || '',
        has_meal: r.has_meal || false,
        has_bath: r.has_bath || false,
        has_pickup: r.has_pickup || false,
        is_summer: r.is_summer || false,
      });
    } catch (e) {
      setError('支援記録の取得に失敗しました');
    } finally {
      setInitialLoading(false);
    }
  };


  const fetchData = async () => {
    try {
      const [clientsRes, facilitiesRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/facilities`)
      ]);
      setClients(clientsRes.data);
      setFacilities(facilitiesRes.data);
      
      // URLパラメータのclient_idからfacility_idを自動設定
      const paramClientId = searchParams.get('client_id');
      if (paramClientId) {
        const paramClient = clientsRes.data.find(c => c.id === paramClientId);
        if (paramClient && paramClient.facility_id) {
          setFormData(prev => ({ ...prev, facility_id: paramClient.facility_id }));
          return;
        }
      }
      if (facilitiesRes.data.length > 0 && !formData.facility_id) {
        setFormData(prev => ({ ...prev, facility_id: facilitiesRes.data[0].id }));
      }
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // 利用者を選択したら、その利用者のfacility_idを自動セット
      if (field === 'client_id' && value) {
        const selectedClient = clients.find(c => c.id === value);
        if (selectedClient && selectedClient.facility_id) {
          updated.facility_id = selectedClient.facility_id;
        }
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.client_id) {
      setError('利用者を選択してください');
      setLoading(false);
      return;
    }

    try {
      const payload = { ...formData, signature_data: signatureData };
      if (isEditMode) {
        await axios.put(`${API}/support-records/${recordId}`, payload);
      } else {
        await axios.post(`${API}/support-records`, payload);
      }
      navigate('/records');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureSave = (data) => {
    setSignatureData(data);
    setShowSignature(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="record-form-page">
      {/* ヘッダー */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/records')}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {isEditMode ? '支援記録編集' : '支援記録作成'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEditMode ? '支援記録を編集します' : '新しい支援記録を作成します'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div 
            className="flex items-center gap-2 p-4 rounded-lg bg-[hsl(var(--error-bg))] text-[hsl(var(--error))]"
            data-testid="form-error-message"
          >
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {/* 基本情報 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>基本情報</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="client_id">利用者 *</Label>
              <Select 
                value={formData.client_id} 
                onValueChange={(value) => handleChange('client_id', value)}
              >
                <SelectTrigger data-testid="record-client-select">
                  <SelectValue placeholder="利用者を選択" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} ({c.name_kana})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="facility_id">事業所</Label>
              <Select 
                value={formData.facility_id} 
                onValueChange={(value) => handleChange('facility_id', value)}
              >
                <SelectTrigger data-testid="record-facility-select">
                  <SelectValue placeholder="事業所を選択" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_type">サービス種別 *</Label>
              <Select 
                value={formData.service_type} 
                onValueChange={(value) => handleChange('service_type', value)}
              >
                <SelectTrigger data-testid="record-service-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="service_date">支援日 *</Label>
              <Input
                id="service_date"
                type="date"
                value={formData.service_date}
                onChange={(e) => handleChange('service_date', e.target.value)}
                required
                data-testid="record-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_time">開始時刻 *</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => handleChange('start_time', e.target.value)}
                required
                data-testid="record-start-time-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time">終了時刻 *</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => handleChange('end_time', e.target.value)}
                required
                data-testid="record-end-time-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* 担当者情報 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>担当者情報</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="staff_name">担当職員名 *</Label>
              <Input
                id="staff_name"
                value={formData.staff_name}
                onChange={(e) => handleChange('staff_name', e.target.value)}
                placeholder="担当職員名"
                required
                data-testid="record-staff-name-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* 支援内容 */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>支援内容</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="content">支援内容 *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="支援内容を詳しく記入してください"
                rows={6}
                required
                data-testid="record-content-input"
              />
            </div>

            {/* 加算項目 */}
            <div className="space-y-2">
              <Label>加算項目</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: 'has_meal', label: '食事提供', unit: '30単位' },
                  { key: 'has_bath', label: '入浴支援', unit: '40単位' },
                  { key: 'has_pickup', label: '送迎', unit: '27/54単位' },
                  { key: 'is_summer', label: '夏季加算', unit: '90単位' },
                ].map(item => (
                  <label key={item.key} className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-colors ${formData[item.key] ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`} data-testid={`addon-${item.key}`}>
                    <input
                      type="checkbox"
                      checked={formData[item.key]}
                      onChange={(e) => handleChange(item.key, e.target.checked)}
                      className="rounded border-border"
                    />
                    <div>
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.unit}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">備考</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="特記事項があれば入力してください"
                rows={3}
                data-testid="record-notes-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* 電子サイン */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle>電子サイン（任意）</CardTitle>
          </CardHeader>
          <CardContent>
            {showSignature ? (
              <SignaturePad 
                onSave={handleSignatureSave}
                onCancel={() => setShowSignature(false)}
              />
            ) : signatureData ? (
              <div className="space-y-3">
                <div className="border border-border rounded-lg p-4 bg-white">
                  <img 
                    src={signatureData} 
                    alt="署名" 
                    className="max-h-24 mx-auto"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setShowSignature(true)}
                    data-testid="change-signature-button"
                  >
                    <PenLine size={16} className="mr-2" />
                    署名を変更
                  </Button>
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setSignatureData('')}
                    data-testid="remove-signature-button"
                  >
                    署名を削除
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                type="button"
                variant="outline"
                onClick={() => setShowSignature(true)}
                data-testid="add-signature-button"
              >
                <PenLine size={16} className="mr-2" />
                署名を追加
              </Button>
            )}
          </CardContent>
        </Card>

        {/* 送信ボタン */}
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline"
            onClick={() => navigate('/records')}
            data-testid="cancel-button"
          >
            キャンセル
          </Button>
          <Button 
            type="submit" 
            disabled={loading}
            className="btn-active"
            data-testid="submit-button"
          >
            <Save size={18} className="mr-2" />
            {loading ? '保存中...' : '保存する'}
          </Button>
        </div>
      </form>
    </div>
  );
}
